import React from 'react';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { observer } from 'src/utils/mobx-react';
import { Calendar } from 'src/domains/casino/webview/bonus-engine/campaign/Calendar';
import { MultiOptionSelect } from 'src/domains/common/components/multi-option-select/multi-option-select';
import { useCommon } from 'src/AppState/Common';
import { UK_TIMEZONE } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { BonusCreationState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreation.state';
import { SchedulingSection } from 'src/domains/casino/webview/bonus-engine/common/components/SchedulingSection';
import { RewardSportsPicker } from 'src/domains/casino/webview/bonus-engine/common/components/RewardSportsPicker';
import { BET_TYPE_OPTIONS, EXPIRY_OPTIONS } from 'src/domains/casino/webview/bonus-engine/common/const';

interface SportsBookSectionStatePropsType {
    bonusCreationId?: string;
}

export const SportsBookSection = observer(
    'SportsBookSection',
    ({ bonusCreationId }: SportsBookSectionStatePropsType): JSX.Element => {
        const common = useCommon();
        const state = BonusCreationState.get(common, bonusCreationId);
        const {
            rewardConditionsState: { sportsBookSectionState },
            editionDisabled,
        } = state;

        return (
            <>
                <section
                    data-test='betting-type'
                    className='flex flex-col gap-6'
                >
                    <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Betting Types & Odds Criteria</h2>

                    <div className='flex flex-col gap-4'>
                        <MultiOptionSelect
                            label='Bet Type'
                            nodes={BET_TYPE_OPTIONS}
                            formInputState={sportsBookSectionState.betTypeForm}
                            errors={sportsBookSectionState.betTypeForm.errorsForView}
                            disabled={editionDisabled}
                        />

                        <div className='grid grid-cols-2 gap-4'>
                            <Input
                                description='Min value is £1'
                                formInputState={sportsBookSectionState.rewardValueForm}
                                label='Reward Value'
                                placeholder='Type'
                                errors={sportsBookSectionState.rewardValueForm.errorsForView}
                                disabled={editionDisabled}
                            />
                            <Input
                                description='Max value is 100.00'
                                formInputState={sportsBookSectionState.minOddsFrom}
                                label='Min Odds'
                                placeholder='Type'
                                errors={sportsBookSectionState.minOddsFrom.errorsForView}
                                disabled={editionDisabled}
                            />
                        </div>
                    </div>
                </section>

                <section
                    data-test='reward-expiry'
                    className='flex flex-col gap-6'
                >
                    <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Reward Expiry</h2>
                    <div className='flex flex-col gap-4'>
                        <div className='grid grid-cols-2 gap-4'>
                            <Select
                                label='Type'
                                placeholder='Select'
                                values={EXPIRY_OPTIONS}
                                formInputState={sportsBookSectionState.expiryForm}
                                errors={sportsBookSectionState.expiryForm.errorsForView}
                                disabled={editionDisabled}
                            />

                            {sportsBookSectionState.expiryForm.value === 'relative-expiry' ? (
                                <Input
                                    label='Number of days'
                                    placeholder='Number'
                                    formInputState={sportsBookSectionState.relativeExpiryDays}
                                    description='Enter a number greater than 0'
                                    errors={sportsBookSectionState.relativeExpiryDays.errorsForView}
                                    disabled={editionDisabled}
                                />
                            ) : (
                                <Calendar
                                    label='End Date & Time (UK time)'
                                    firstDayOfWeek='monday'
                                    timezone={UK_TIMEZONE}
                                    description='After reward date if scheduled'
                                    formInputState={sportsBookSectionState.endDateTimeForm}
                                    placeholder='*Current Date*'
                                    showTimePicker={true}
                                    errors={sportsBookSectionState.endDateTimeForm.errorsForView}
                                    disabled={editionDisabled}
                                />
                            )}
                        </div>
                    </div>
                </section>

                <SchedulingSection
                    state={sportsBookSectionState}
                    editionDisabled={editionDisabled}
                />

                <section
                    data-test='sports-picker'
                    className='flex flex-col gap-6'
                >
                    <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>
                        {'Eligible: Sport > Competition > Event > Market'}
                    </h2>
                    <RewardSportsPicker
                        state={state}
                        disabled={editionDisabled}
                    />
                </section>
            </>
        );
    }
);
